import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './PaymentResult.css';

function PaymentResult() {
  const [searchParams] = useSearchParams();
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      // Get status and data from URL parameters
      const status = searchParams.get('status');
      const encodedData = searchParams.get('data');

      if (encodedData) {
        const data = JSON.parse(decodeURIComponent(encodedData));
        setResultData({ status, data });
      } else {
        setResultData({ status, data: {} });
      }
    } catch (error) {
      console.error('Error parsing payment result data:', error);
      setResultData({ status: 'error', data: {} });
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="payment-result-container loading">
        <h1>Processing payment result...</h1>
        <div className="spinner"></div>
      </div>
    );
  }

  // If no result data or status is found
  if (!resultData || !resultData.status) {
    return (
      <div className="payment-result-container error">
        <h1>Invalid Payment Response</h1>
        <p>We couldn't process the payment response. Please contact our support team.</p>
        <Link to="/" className="home-button">Return to Home</Link>
      </div>
    );
  }

  // Handle different status cases
  switch (resultData.status) {
    case 'success':
      return (
        <div className="payment-result-container success">
          <div className="result-icon success-icon">✓</div>
          <h1>Payment Successful!</h1>
          <div className="result-details">
            <p>Your payment has been processed successfully.</p>
            <div className="details-table">
              <div className="details-row">
                <span>Invoice Number:</span>
                <span>{resultData.data.invoice_num}</span>
              </div>
              <div className="details-row">
                <span>Transaction ID:</span>
                <span>{resultData.data.transaction_id}</span>
              </div>
              <div className="details-row">
                <span>Amount Paid:</span>
                <span>${parseFloat(resultData.data.amount).toFixed(2)}</span>
              </div>
              <div className="details-row">
                <span>Date:</span>
                <span>{resultData.data.date}</span>
              </div>
            </div>
          </div>
          <p className="thank-you-message">Thank you for your payment, {resultData.data.name}!</p>
          <p className="receipt-message">A receipt has been sent to your email address.</p>
          <div className="action-buttons">
            <Link to="/" className="home-button">Return to Home</Link>
          </div>
        </div>
      );

    case 'failed':
      return (
        <div className="payment-result-container failed">
          <div className="result-icon failed-icon">✗</div>
          <h1>Payment Failed</h1>
          <div className="result-details">
            <p>We were unable to process your payment.</p>
            <p>Reason: {resultData.data.reason || 'Unknown error'}</p>
            <p>Error Code: {resultData.data.code || 'None'}</p>
          </div>
          <div className="action-buttons">
            <Link to={`/pay-bill/${resultData.data.qrcode || ''}`} className="retry-button">Try Again</Link>
            <Link to="/" className="home-button">Return to Home</Link>
          </div>
        </div>
      );

    case 'error_update':
    case 'error_match':
      return (
        <div className="payment-result-container warning">
          <div className="result-icon warning-icon">!</div>
          <h1>Payment Processing Issue</h1>
          <div className="result-details">
            <p>Your payment may have been successful, but we encountered an issue updating our records.</p>
            <p>Invoice Number: {resultData.data.invoice_num}</p>
            <p>Please contact our support team with this information.</p>
          </div>
          <div className="action-buttons">
            <Link to="/" className="home-button">Return to Home</Link>
          </div>
        </div>
      );

    default:
      return (
        <div className="payment-result-container error">
          <div className="result-icon error-icon">?</div>
          <h1>Unknown Payment Status</h1>
          <p>We received an unknown payment status from our payment processor.</p>
          <p>Please contact our support team for assistance.</p>
          <div className="action-buttons">
            <Link to="/" className="home-button">Return to Home</Link>
          </div>
        </div>
      );
  }
}

export default PaymentResult;